/*
 * What changed between two short texts, word by word (e.g. an item name before and after its tags
 * are updated), for showing the change in place: removed words struck through, added ones marked.
 */

export interface DiffPart {
    text: string;
    change: "same" | "removed" | "added";
}

/** Words, and the spaces, line breaks and punctuation between them, each its own token. */
const tokens = (text: string) =>
    text
        .replaceAll("\r\n", "\n")
        .split(/(\s+|[(),&])/)
        .filter(Boolean);

/** `before` to `after` as runs of kept, removed and added text (longest common run of words kept). */
export function wordDiff(before: string, after: string): DiffPart[] {
    const a = tokens(before);
    const b = tokens(after);
    // lcs[i][j]: how many tokens a[i..] and b[j..] have in common, in order.
    const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            const row = lcs[i] as number[];
            row[j] = a[i] === b[j] ? (lcs[i + 1]?.[j + 1] ?? 0) + 1 : Math.max(lcs[i + 1]?.[j] ?? 0, row[j + 1] ?? 0);
        }
    }
    const steps: DiffPart[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) {
            steps.push({ text: a[i++] as string, change: "same" });
            j++;
        } else if (i < a.length && (j >= b.length || (lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0))) {
            steps.push({ text: a[i++] as string, change: "removed" });
        } else {
            steps.push({ text: b[j++] as string, change: "added" });
        }
    }
    return grouped(steps);
}

/**
 * Each change as one run of removed text then one of added text ("Supply Only" -> "Includes
 * Installation", not word by word): spaces between changed words count as part of the change.
 */
function grouped(steps: DiffPart[]): DiffPart[] {
    const parts: DiffPart[] = [];
    let removed = "";
    let added = "";
    const flush = () => {
        if (removed) parts.push({ text: removed, change: "removed" });
        if (added) parts.push({ text: added, change: "added" });
        removed = "";
        added = "";
    };
    steps.forEach((step, k) => {
        const between = step.change === "same" && !step.text.trim() && (removed || added) !== "" && steps[k + 1]?.change !== "same" && k + 1 < steps.length;
        if (step.change === "removed" || between) removed += step.text;
        if (step.change === "added" || between) added += step.text;
        if (step.change !== "same" || between) return;
        flush();
        const last = parts.at(-1);
        if (last?.change === "same") last.text += step.text;
        else parts.push({ ...step });
    });
    flush();
    return parts;
}

/**
 * `before` to `after` line by line, each line compared with its own (so a change on one line never
 * runs into the next): lines kept in both are lined up; between them, the lines that changed are
 * paired in order and compared word by word, and any left over are a line taken out (struck
 * through whole) or added (marked whole). One list of parts per line, in order.
 */
export function lineDiff(before: string, after: string): DiffPart[][] {
    const a = before.split(/\r?\n/);
    const b = after.split(/\r?\n/);
    // lcs[i][j]: how many lines a[i..] and b[j..] have in common, in order.
    const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            const row = lcs[i] as number[];
            row[j] = a[i] === b[j] ? (lcs[i + 1]?.[j + 1] ?? 0) + 1 : Math.max(lcs[i + 1]?.[j] ?? 0, row[j + 1] ?? 0);
        }
    }
    const out: DiffPart[][] = [];
    // The lines changed since the last one kept: paired in order, the rest taken out or added.
    let gone: string[] = [];
    let added: string[] = [];
    const flush = () => {
        for (let k = 0; k < Math.max(gone.length, added.length); k++) {
            const was = gone[k];
            const is = added[k];
            if (was !== undefined && is !== undefined) out.push(wordDiff(was, is));
            else if (was !== undefined) out.push(was ? [{ text: was, change: "removed" }] : []);
            else if (is !== undefined) out.push(is ? [{ text: is, change: "added" }] : []);
        }
        gone = [];
        added = [];
    };
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) {
            flush();
            out.push(a[i] ? [{ text: a[i] as string, change: "same" }] : []);
            i++;
            j++;
        } else if (i < a.length && (j >= b.length || (lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0))) {
            gone.push(a[i++] as string);
        } else {
            added.push(b[j++] as string);
        }
    }
    flush();
    return out;
}
